import { Box } from '@mui/material';
import { WarningRounded } from '@mui/icons-material';
import React, { forwardRef, ReactElement } from 'react';
import { useUID } from 'react-uid';

export interface TextAreaProps
  extends Omit<JSX.IntrinsicElements['textarea'], 'ref'> {
  name?: string;
  label?: string;
  hasError?: boolean;
  hideWarningIcon?: boolean;
  children?: React.ReactNode;
  minHeight?: number;
  maxHeight?: number;
}

export const TextArea = forwardRef(
  (
    {
      name,
      label,
      hasError = false,
      hideWarningIcon = false,
      children,
      minHeight = 0,
      maxHeight = 240,
      ...textareaProps
    }: TextAreaProps,
    ref?: React.Ref<HTMLTextAreaElement>,
  ): ReactElement => {
    const id = name ? `${name}-${useUID()}` : useUID();

    return (
      <>
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            '& .label': {
              fontSize: 14,
              fontWeight: 600,
              color: theme =>
                hasError
                  ? theme.palette.error.main
                  : theme.palette.text.primary,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)',
            },
            '& .input': {
              width: '100%',
              minHeight: minHeight,
              maxHeight: maxHeight,
              background: theme => theme.palette.neutral.main,
              border: '1px solid',
              borderRadius: 4,
              outline: 'none',
              fontFamily: 'Poppins',
              borderColor: theme =>
                hasError
                  ? theme.palette.error.light
                  : theme.palette.neutral.dark,
              padding: '15px 10px',
              paddingRight: hasError ? 47 : 10,
              '&::placeholder': {
                color: theme => theme.palette.neutral.darker,
              },
              '&:focus': {
                borderColor: theme => theme.palette.primary.main,
                boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`,
              },
              '&:focus + *': {
                borderColor: theme => theme.palette.primary.main,
                borderWidth: 2,
              },
            },
          }}
        >
          {label && (
            <label htmlFor={id} className="label">
              {label}
            </label>
          )}
          <Box position="relative" lineHeight={0}>
            {hasError && !hideWarningIcon && (
              <Box position="absolute" bottom={10} right={10} lineHeight={0}>
                <WarningRounded
                  sx={{ color: theme => theme.palette.error.main }}
                />
              </Box>
            )}
            <textarea
              name={name}
              id={id}
              className="input"
              ref={ref}
              aria-invalid={hasError}
              {...textareaProps}
              onChange={e => {
                const el = e.target as HTMLTextAreaElement;
                el.style.height = 'auto';

                const newHeight = Math.max(
                  minHeight,
                  // Add 2 pixels for the vertical borders
                  Math.min(maxHeight, el.scrollHeight + 2),
                );
                el.style.height = `${newHeight}px`;

                if (textareaProps.onChange)
                  textareaProps.onChange(e);

              }}
            />
            {children}
          </Box>
        </Box>
      </>
    );
  },
);
